import { useEffect } from "react";
import { useLocalSearchParams } from "expo-router";
import { useFleetIncident, useTenantIncident, type IncidentDetail } from "@visioni/a3s-monitoring/app";
import { Text } from "@src/components";
import { useReplaceTenantRoute } from "@src/features/tenants";
import { EMPTY, formatInstant } from "../format";
import {
  activationHref,
  clientIncidentsHref,
  fleetClientHref,
  incidentHref,
  operatorNoteAnchor,
  operatorNotesAroundHref,
  tenantIncidentsHref,
} from "../routes";
import { useActivationScopeOptions } from "../hooks/useActivationScopeOptions";
import { useDirectoryTenantName } from "../hooks/useDirectoryTenantName";
import { IncidentDetailView } from "../components/IncidentDetailView";
import { PatriotToolsPage } from "../components/PatriotToolsPage";
import { QueryNotice } from "../components/QueryNotice";

const rawId = (id: string) => id;

/** When and where: what people call an incident by. Its code sits beside it to quote and copy (ADR-0085). */
const titleOf = (incident: IncidentDetail, siteLabel?: (siteId: string) => string) => {
  const when = `Genuine incident, ${formatInstant(incident.startedAt)}`;
  return siteLabel !== undefined && incident.a3sSite !== undefined ? `${when} at ${siteLabel(incident.a3sSite)}` : when;
};

const IncidentCode = ({ incident }: { readonly incident: IncidentDetail }) => (
  <Text textStyle="sm/regular" color="textMuted" selectable>
    {incident.id}
  </Text>
);

const subtitleOf = (incident: IncidentDetail) =>
  [`client ${incident.clientNo}`, incident.policeCalled ? "police called" : undefined]
    .filter((part) => part !== undefined)
    .join(" · ");

/**
 * Activations link to the fleet page, which serves a ticket whichever tenant
 * holds it and redirects to that tenant's own page when the directory knows it:
 * an incident's activations can come from cameras a3s does not mark.
 */
const activationLinks = (tenantId: string) => (ticketNo: string, noteId?: string) =>
  activationHref(tenantId, ticketNo, undefined, noteId !== undefined ? operatorNoteAnchor(noteId) : undefined);

const viewerTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** The client links, in the viewer's zone like every time on the page. */
const clientLinks = (tenantId: string, incident: IncidentDetail) => {
  const timeZone = viewerTimeZone();
  return {
    clientHref: fleetClientHref(tenantId, incident.clientNo),
    clientIncidentsHref: clientIncidentsHref(tenantId, incident.clientNo, { at: incident.startedAt, timeZone }),
    ticketlessCallHref: (call: { readonly at: string }) =>
      operatorNotesAroundHref(tenantId, incident.clientNo, call.at, timeZone),
  };
};

/** One incident from the tenant route, which serves it only when it is counted under this tenant. */
export function TenantIncidentScreen() {
  const replaceRoute = useReplaceTenantRoute();
  const { tenantId, incidentId = "" } = useLocalSearchParams<{ tenantId: string; incidentId?: string }>();
  const { incident, isLoading, error, refetch } = useTenantIncident(incidentId);
  const scope = useActivationScopeOptions();
  const tenantName = useDirectoryTenantName();

  // A code an incident had before a merge or a late, earlier call still opens it, under its current one.
  const renamedTo = incident !== undefined && incident.id !== incidentId ? incident.id : undefined;
  useEffect(() => {
    if (renamedTo !== undefined) replaceRoute(incidentHref(tenantId, renamedTo, tenantId));
  }, [renamedTo, tenantId, replaceRoute]);

  return (
    <PatriotToolsPage
      title={incident !== undefined ? titleOf(incident, scope.siteLabel) : "Genuine incident"}
      {...(incident !== undefined && {
        subtitle: subtitleOf(incident),
        titleAccessory: <IncidentCode incident={incident} />,
      })}
    >
      {incident === undefined ?
        <QueryNotice isLoading={isLoading} error={error} onRetry={refetch} />
      : <IncidentDetailView
          incident={incident}
          tenantLabel={tenantName(tenantId) ?? tenantId}
          siteLabel={scope.siteLabel}
          cameraLabel={scope.cameraLabel}
          activationHref={activationLinks(tenantId)}
          {...clientLinks(tenantId, incident)}
          tenantIncidentsHref={(scope) =>
            tenantIncidentsHref(tenantId, scope, { at: incident.startedAt, timeZone: viewerTimeZone() })
          }
        />
      }
    </PatriotToolsPage>
  );
}

/**
 * One incident by id alone, for one counted under a tenant the directory does
 * not hold, or under none. One whose tenant it does hold is redirected there.
 */
export function FleetIncidentScreen() {
  const replaceRoute = useReplaceTenantRoute();
  const { tenantId, incidentId = "" } = useLocalSearchParams<{ tenantId: string; incidentId?: string }>();
  const { incident, isLoading, error, refetch } = useFleetIncident(incidentId);
  const tenantName = useDirectoryTenantName();

  const owner = incident?.a3sTenant;
  const known = owner !== undefined && tenantName(owner) !== undefined ? owner : undefined;

  // Also moves a code the incident had before a merge or a late, earlier call to its current one.
  const currentId = incident?.id ?? incidentId;
  useEffect(() => {
    if (known !== undefined || currentId !== incidentId) replaceRoute(incidentHref(tenantId, currentId, known));
  }, [known, tenantId, incidentId, currentId, replaceRoute]);

  return (
    <PatriotToolsPage
      title={incident !== undefined ? titleOf(incident) : "Genuine incident"}
      {...(incident !== undefined && {
        subtitle: subtitleOf(incident),
        titleAccessory: <IncidentCode incident={incident} />,
      })}
    >
      {incident === undefined || known !== undefined ?
        <QueryNotice isLoading={isLoading || known !== undefined} error={error} onRetry={refetch} />
      : <IncidentDetailView
          incident={incident}
          tenantLabel={owner ?? EMPTY}
          siteLabel={rawId}
          cameraLabel={rawId}
          activationHref={activationLinks(tenantId)}
          {...clientLinks(tenantId, incident)}
        />
      }
    </PatriotToolsPage>
  );
}
